import { ArrowLeft, Clock, Headset, RotateCcw, Smile } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { env } from '../../../config/env';
import { PaymentStatusPanel } from './PaymentStatusPanel';
import type { PaymentError } from '../utils/errors';

interface PaymentErrorScreenProps {
  error: PaymentError;
  onRetry?: () => void;
  onBack?: () => void;
}

/**
 * The screen a decrypt failure or unresolved country used to have nothing
 * to show for itself — Payment.jsx's catch block only console.error'd and
 * the page rendered a header over nothing (the plan's §1, Critical). This
 * is what renders instead, for every unrecoverable config/gateway error —
 * through PaymentStatusPanel, never a bare "error" visual.
 */
export function PaymentErrorScreen({
  error,
  onRetry,
  onBack,
}: PaymentErrorScreenProps) {
  const navigate = useNavigate();
  const supportPhone = env.VITE_SUPPORT_PHONE;

  // "Contact support" is only ever offered — as primary or secondary —
  // when a real number is configured for this deployment. Before this it
  // showed unconditionally but didn't actually contact anyone; the button
  // just went back. Not every environment has a support line yet (see
  // src/config/env.ts), so this has to check, not assume.
  const contactSupport = supportPhone
    ? { label: 'Contact support', icon: Headset, href: `tel:${supportPhone}` }
    : null;
  const goBack = {
    label: 'Go back',
    icon: ArrowLeft,
    onClick: onBack ?? (() => navigate(-1)),
  };

  let primaryAction;
  let secondaryAction;

  if (onRetry) {
    primaryAction = { label: 'Try again', icon: RotateCcw, onClick: onRetry };
    secondaryAction = contactSupport ?? (onBack ? goBack : undefined);
  } else if (contactSupport) {
    primaryAction = contactSupport;
    secondaryAction = onBack ? goBack : undefined;
  } else {
    primaryAction = goBack;
    secondaryAction = undefined;
  }

  return (
    <PaymentStatusPanel
      icon={error.code === 'TIMEOUT' ? Clock : Smile}
      title={friendlyTitle(error)}
      message={friendlyMessage(error)}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
    />
  );
}

function friendlyTitle(error: PaymentError): string {
  switch (error.code) {
    case 'TIMEOUT':
      return "We're still checking on your payment";
    case 'ALREADY_PAID':
      return 'This booking is already paid for';
    case 'USER_CANCELLED':
      return 'Payment cancelled';
    default:
      return "We couldn't complete your payment";
  }
}

function friendlyMessage(error: PaymentError): string {
  switch (error.code) {
    case 'NETWORK_ERROR':
      return "We couldn't reach the payment service just now. Please check your connection and try again.";
    case 'TIMEOUT':
      return "We haven't heard back yet. If you were charged, we'll confirm your booking as soon as it clears — no need to pay again.";
    case 'GATEWAY_DECLINED':
      return 'Your bank or payment provider wasn’t able to approve this. No amount has been charged — feel free to try again.';
    case 'ALREADY_PAID':
      return 'Looks like this one’s already been taken care of — you can head back to your booking.';
    case 'USER_CANCELLED':
      return 'No worries — if anything was charged, it’ll be refunded automatically within 5–7 days. You can pick up right where you left off.';
    case 'CONFIG_ERROR':
    case 'INVALID_ORDER':
      return 'This payment link looks incomplete or out of date. Heading back to the booking page should sort it out.';
    default:
      return "Something didn't go quite right on our end. Our team's already been notified — please try again in a moment.";
  }
}
